import Select from '@material-ui/core/Select';
import clsx from 'clsx';
import React, { ReactNode } from 'react';
import NumberFormat from 'react-number-format';
import { removePropertiesFromObj } from '../../helpers/object';
import StepFormIncDecButton from '../StepForm/StepFormIncDecButton';
import styles from './FormElement.module.scss';
import { Controller } from 'react-hook-form';

interface Common {
  inputRef?: any;
  label?: string;
  subLabel?: string | ReactNode;
  error?: any;
  divStyle?: any;
  errorAtTop?: boolean;
  variant?: 'underlined' | 'outlined';
}

interface InputProps
  extends Common,
    Partial<React.InputHTMLAttributes<HTMLInputElement>> {
  elementType: 'text' | 'number' | 'date' | 'email' | 'password';
}

interface TextAreaProps
  extends Common,
    Partial<React.TextareaHTMLAttributes<HTMLTextAreaElement>> {
  elementType: 'textarea';
}

interface CheckboxRadioProps
  extends Common,
    Partial<React.InputHTMLAttributes<HTMLInputElement>> {
  elementType: 'checkbox' | 'radio';
  elements: { name: string; value: string }[];
}

interface SelectProps extends Common {
  elementType: 'select';
  options: { name: string; value: string }[];
  placeholder?: string;
  name: string;
}

interface NumberButtonsProps
  extends Common,
    Partial<React.InputHTMLAttributes<HTMLInputElement>> {
  elementType: 'numberButtons';
  control: any;
  name: string;
  min: number;
  max: number;
  rules?: Record<string, any>;
}

interface PriceProps extends Common {
  elementType: 'price';
  control: any;
  name: string;
  rules?: Record<string, any>;
}

interface CustomElementProps extends Common {
  elementType: 'custom';
  children?: React.ReactNode;
}

export default function FormElement({
  inputRef,
  label,
  error,
  variant,
  subLabel,
  errorAtTop,
  ...props
}:
  | InputProps
  | TextAreaProps
  | SelectProps
  | CheckboxRadioProps
  | NumberButtonsProps
  | CustomElementProps
  | PriceProps) {
  const getErrorMessage = (error: any) => {
    let errorMessage = 'Este campo es obligatorio.';

    if (typeof error === 'string') {
      errorMessage = error;
    }

    return !!error && <span className={styles.error}>{errorMessage}</span>;
  };

  return (
    <div
      style={props.divStyle}
      className={clsx(
        styles.container,
        error && styles.containerError,
        variant === 'outlined' && styles.containerVariantOutlined,
        variant === 'underlined' && styles.containerVariantUnderlined
      )}
    >
      {label && (
        <label className={styles.label}>
          {label}
          {subLabel && <span>{subLabel}</span>}
        </label>
      )}

      {errorAtTop && getErrorMessage(error)}

      {props.elementType === 'select' ? (
        <Select
          {...removePropertiesFromObj(props, ['elementType', 'options'])}
          native
          error={!!error}
          inputRef={inputRef}
          inputProps={{
            name: props.name,
          }}
        >
          <option disabled value="">
            {props.placeholder ?? label}
          </option>
          {props.options.map(({ name, value }) => (
            <option key={value} value={value}>
              {name}
            </option>
          ))}
        </Select>
      ) : props.elementType === 'textarea' ? (
        <textarea
          {...removePropertiesFromObj(props, ['elementType'])}
          ref={inputRef}
        />
      ) : props.elementType === 'checkbox' || props.elementType === 'radio' ? (
        props.elements.map((el) => (
          <label key={el.value} className={styles.checkboxLabel}>
            <input
              name={props.name}
              value={el.value}
              type={props.elementType}
              {...removePropertiesFromObj(props, ['elementType', 'elements'])}
              ref={inputRef}
            />
            <p />
            <span>{el.name}</span>
          </label>
        ))
      ) : props.elementType === 'price' ? (
        <Controller
          name={props.name}
          control={props.control}
          rules={props.rules}
          render={({ onChange, value }) => (
            <div className={styles.inputWithPrefix}>
              <span>$</span>
              <NumberFormat
                name={props.name}
                value={value}
                onValueChange={({ value }) => onChange(value)}
                displayType="input"
                thousandSeparator={true}
              />
            </div>
          )}
        />
      ) : props.elementType === 'numberButtons' ? (
        <Controller
          name={props.name}
          control={props.control}
          rules={props.rules}
          render={({ onChange, value }) => (
            <StepFormIncDecButton
              onChange={(value) => onChange(value)}
              value={value}
              min={props.min}
              max={props.max}
            />
          )}
        />
      ) : props.elementType === 'custom' ? (
        props.children
      ) : (
        <input
          {...removePropertiesFromObj(props, ['elementType'])}
          type={props.elementType}
          ref={inputRef}
        />
      )}
      {!errorAtTop && getErrorMessage(error)}
    </div>
  );
}
